import { useEffect, useRef, useState } from "react";
import {
  parseProject,
  type Project,
  type ImportReport,
  type ImportResult,
  type ProjectArchiveUpload,
} from "@proof/core";
import { Select, Button } from "./components";
import { ImportProgress } from "./ImportProgress";

export interface ProjectArchiveImportServices {
  startUpload(
    input: { filename: string; bytes: number },
    signal: AbortSignal,
  ): Promise<ProjectArchiveUpload>;
  uploadStatus(id: string, signal: AbortSignal): Promise<ProjectArchiveUpload>;
  uploadChunk(
    id: string,
    offset: number,
    chunk: Blob,
    signal: AbortSignal,
  ): Promise<ProjectArchiveUpload>;
  importProject(
    id: string,
    entryId: string,
    template: string | undefined,
    signal: AbortSignal,
  ): Promise<ImportResult>;
  removeUpload(id: string, signal: AbortSignal): Promise<unknown>;
}
export interface ProjectArchiveImportProps {
  services: ProjectArchiveImportServices;
  onImport(
    project: Project,
    report: ImportReport,
    signal: AbortSignal,
  ): void | Promise<void>;
  onOpen?: () => void;
  initialFile?: File;
  jsonTenant?: string;
}
type Row = {
  state: "imported" | "failed";
  name: string;
  error?: string;
  warnings?: string[];
};
const size = (bytes: number) =>
  bytes >= 1024 ** 3
    ? `${(bytes / 1024 ** 3).toFixed(2)} GiB`
    : `${(bytes / 1024 ** 2).toFixed(1)} MiB`;
/** A host controls persistence. Upload and discovery never call onImport. */
export function ProjectArchiveImport({
  services,
  onImport,
  onOpen,
  initialFile,
  jsonTenant,
}: ProjectArchiveImportProps) {
  const [upload, setUpload] = useState<ProjectArchiveUpload>(),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [phase, setPhase] = useState(""),
    [rows, setRows] = useState<Record<string, Row>>({}),
    [selected, setSelected] = useState<Set<string>>(new Set()),
    [templates, setTemplates] = useState<Record<string, string>>({}),
    [progress, setProgress] = useState({ done: 0, total: 0 });
  const controller = useRef<AbortController>(undefined),
    fileRef = useRef<File>(undefined),
    session = useRef<ProjectArchiveUpload>(undefined),
    latest = useRef({ services, onImport });
  latest.current = { services, onImport };
  const uploadFile = async (file: File, resume = false) => {
    controller.current?.abort();
    const abort = new AbortController();
    controller.current = abort;
    fileRef.current = file;
    setError("");
    setBusy(true);
    setPhase("Uploading archive");
    try {
      if (
        !/\.zip$/i.test(file.name) ||
        file.size < 1 ||
        file.size > 8 * 1024 ** 3
      )
        throw new Error("Choose a nonempty ZIP archive up to 8 GiB.");
      if (!resume && session.current) {
        await services.removeUpload(session.current.id, abort.signal);
        session.current = undefined;
        setUpload(undefined);
      }
      let current =
        resume && session.current
          ? await services.uploadStatus(session.current.id, abort.signal)
          : await services.startUpload(
              { filename: file.name, bytes: file.size },
              abort.signal,
            );
      if (abort.signal.aborted) return;
      session.current = current;
      setUpload(current);
      if (!resume) {
        setRows({});
        setTemplates({});
        setSelected(new Set());
        setProgress({ done: 0, total: 0 });
      }
      while (current.uploadedBytes < file.size) {
        const end = Math.min(
          current.uploadedBytes + current.chunkBytes,
          file.size,
        );
        current = await services.uploadChunk(
          current.id,
          current.uploadedBytes,
          file.slice(current.uploadedBytes, end),
          abort.signal,
        );
        if (abort.signal.aborted) return;
        session.current = current;
        setUpload(current);
      }
      if (!current.catalog) {
        setPhase("Inspecting archive and finding projects");
        current = await services.uploadStatus(current.id, abort.signal);
      }
      if (abort.signal.aborted) return;
      if (!current.catalog?.entries.length)
        throw new Error("No supported projects were found.");
      session.current = current;
      setUpload(current);
      setSelected(new Set(current.catalog.entries.map((entry) => entry.id)));
      setPhase("Review projects before importing");
    } catch (cause) {
      if (!abort.signal.aborted)
        setError(
          cause instanceof Error ? cause.message : "Archive upload failed",
        );
    } finally {
      if (controller.current === abort) {
        setBusy(false);
        controller.current = undefined;
      }
    }
  };
  useEffect(() => {
    let active = true;
    session.current = undefined;
    setUpload(undefined);
    setRows({});
    setSelected(new Set());
    setTemplates({});
    setProgress({ done: 0, total: 0 });
    setError("");
    setPhase("");
    setBusy(false);
    queueMicrotask(() => {
      if (active && initialFile) void uploadFile(initialFile);
    });
    return () => {
      active = false;
      controller.current?.abort();
      const id = session.current?.id;
      if (id)
        void services
          .removeUpload(id, new AbortController().signal)
          .catch(() => {});
    };
  }, [services, initialFile, jsonTenant]);
  const importSelected = async () => {
    const entries =
      upload?.catalog?.entries.filter(
        (entry) =>
          selected.has(entry.id) && rows[entry.id]?.state !== "imported",
      ) || [];
    if (!upload || !entries.length) return;
    const abort = new AbortController();
    controller.current = abort;
    setBusy(true);
    setError("");
    setProgress({ done: 0, total: entries.length });
    setPhase("Importing selected projects");
    try {
      for (let index = 0; index < entries.length; index++) {
        if (abort.signal.aborted) break;
        const entry = entries[index];
        setPhase(`Importing ${index + 1} of ${entries.length}: ${entry.name}`);
        try {
          const result = await services.importProject(
            upload.id,
            entry.id,
            templates[entry.id],
            abort.signal,
          );
          if (abort.signal.aborted) break;
          const project = parseProject(result.project);
          const notes = result.report?.diagnostics;
          if (
            !Array.isArray(notes) ||
            notes.some((note) => typeof note?.message !== "string")
          )
            throw new Error("The host returned an invalid import report.");
          const warnings = notes.map((note) => note.message);
          if (jsonTenant && project.tenantId !== jsonTenant)
            throw new Error(
              "The imported project belongs to a different tenant.",
            );
          await latest.current.onImport(
            structuredClone(project),
            structuredClone(result.report),
            abort.signal,
          );
          if (abort.signal.aborted) break;
          setRows((current) => ({
            ...current,
            [entry.id]: {
              state: "imported",
              name: project.name,
              warnings,
            },
          }));
        } catch (cause) {
          if (abort.signal.aborted) break;
          setRows((current) => ({
            ...current,
            [entry.id]: {
              state: "failed",
              name: entry.name,
              error: cause instanceof Error ? cause.message : "Import failed",
            },
          }));
        }
        setProgress({ done: index + 1, total: entries.length });
      }
      if (!abort.signal.aborted)
        setPhase("Import finished. Review the results below.");
    } finally {
      if (controller.current === abort) {
        setBusy(false);
        controller.current = undefined;
      }
    }
  };
  const imported = Object.values(rows).filter(
      (row) => row.state === "imported",
    ).length,
    failed = Object.values(rows).filter((row) => row.state === "failed").length;
  const remaining =
    upload?.catalog?.entries.filter(
      (entry) => selected.has(entry.id) && rows[entry.id]?.state !== "imported",
    ).length || 0;
  return (
    <section aria-label="Project archive import">
      <p>
        Upload a ZIP containing project folders, portable packages or documents.
        A containing root folder is supported. Review and select projects before
        adding them.
      </p>
      <label className="field">
        <span>Project archive ZIP</span>
        <input
          aria-label="Project archive ZIP"
          type="file"
          accept=".zip"
          disabled={busy}
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = "";
            if (file) void uploadFile(file);
          }}
        />
        <small>
          Up to 8 GiB, uploaded in small chunks. Projects are processed one at a
          time. Generated files and backup folders are skipped.
        </small>
      </label>
      {upload && (
        <p role="status">
          {upload.filename}: {size(upload.uploadedBytes)} / {size(upload.bytes)}{" "}
          uploaded
        </p>
      )}
      {busy && (
        <ImportProgress
          cancelLabel={`Cancel ${upload?.catalog ? "batch import" : "upload"}`}
          onCancel={() => {
            controller.current?.abort();
            controller.current = undefined;
            setBusy(false);
            setPhase("Cancelled. Completed projects are kept.");
          }}
        >
          <p>{phase}</p>
          {!upload && <progress aria-label="Preparing archive upload" />}
          {upload && (
            <p>
              {upload.filename}: {size(upload.uploadedBytes)} /{" "}
              {size(upload.bytes)} uploaded
            </p>
          )}
          {progress.total > 0 && (
            <p>
              {progress.done} of {progress.total} processed · {imported}{" "}
              imported · {failed} failed
            </p>
          )}
          {upload && !upload.catalog && (
            <progress
              aria-label="Archive upload progress"
              value={upload.uploadedBytes}
              max={upload.bytes}
            />
          )}{" "}
          {progress.total > 0 && (
            <progress
              aria-label="Project import progress"
              value={progress.done}
              max={progress.total}
            />
          )}
        </ImportProgress>
      )}
      {!busy && phase && <p role="status">{phase}</p>}
      {error && (
        <p role="alert" className="error-text">
          {error}
        </p>
      )}
      {!busy && fileRef.current && session.current && !upload?.catalog && (
        <Button onClick={() => void uploadFile(fileRef.current!, true)}>
          Resume upload
        </Button>
      )}
      {upload?.catalog && (
        <>
          <h3>Review archive projects ({upload.catalog.entries.length})</h3>
          <p>
            {imported} imported · {failed} failed ·{" "}
            {upload.catalog.ignoredFiles} backup/generated files skipped
          </p>
          <div className="button-row">
            <Button
              disabled={busy}
              onClick={() =>
                setSelected(
                  new Set(upload.catalog!.entries.map((entry) => entry.id)),
                )
              }
            >
              Select all projects
            </Button>
            <Button disabled={busy} onClick={() => setSelected(new Set())}>
              Select no projects
            </Button>
            <Button
              kind="primary"
              disabled={busy || !remaining}
              onClick={() => void importSelected()}
            >
              Import selected projects ({remaining})
            </Button>
            {onOpen && imported > 0 && (
              <Button disabled={busy} onClick={onOpen}>
                View imported projects
              </Button>
            )}
          </div>
          <div className="project-archive-list">
            <table>
              <thead>
                <tr>
                  <th>Import</th>
                  <th>Project</th>
                  <th>Layout</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                {upload.catalog.entries.map((entry) => (
                  <tr key={entry.id}>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`Import ${entry.path}`}
                        checked={selected.has(entry.id)}
                        disabled={busy || rows[entry.id]?.state === "imported"}
                        onChange={(event) =>
                          setSelected((current) => {
                            const next = new Set(current);
                            if (event.target.checked) next.add(entry.id);
                            else next.delete(entry.id);
                            return next;
                          })
                        }
                      />
                    </td>
                    <td>
                      <strong>{entry.name}</strong>
                      <small>{entry.path}</small>
                      <small>
                        {entry.format} · {entry.files} files ·{" "}
                        {size(entry.bytes)}
                      </small>
                    </td>
                    <td>
                      {entry.templates.length > 0 ? (
                        <Select
                          aria-label={`Layout for ${entry.path}`}
                          disabled={
                            busy || rows[entry.id]?.state === "imported"
                          }
                          value={templates[entry.id] || ""}
                          onChange={(event) =>
                            setTemplates((current) => ({
                              ...current,
                              [entry.id]: event.target.value,
                            }))
                          }
                        >
                          <option value="">Suggested layout</option>
                          {entry.templates.map((path) => (
                            <option value={path} key={path}>
                              {path.split("/").at(-1)}
                            </option>
                          ))}
                        </Select>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      {rows[entry.id]?.state === "imported" ? (
                        "Imported"
                      ) : rows[entry.id]?.state === "failed" ? (
                        <span className="error-text">
                          {rows[entry.id].error}
                        </span>
                      ) : (
                        "Ready"
                      )}
                      {!!rows[entry.id]?.warnings?.length && (
                        <details>
                          <summary>
                            {rows[entry.id].warnings!.length} review notes
                          </summary>
                          {rows[entry.id].warnings!.map((warning, index) => (
                            <p key={index}>{warning}</p>
                          ))}
                        </details>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <small>
            Each selected project keeps its own variables, layouts and
            resources. A failed project is reported separately; completed
            projects remain saved. Individual projects retain the existing
            document extraction limits.
          </small>
        </>
      )}
    </section>
  );
}
